type QueryFilter = {
  coluna: string;
  operador: 'eq' | 'neq';
  valor: unknown;
};

type QueryOrder = {
  coluna: string;
  crescente: boolean;
};

type ApiResult<T = unknown> = {
  data: T | null;
  error: Error | null;
};

const API_URL = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');
const AUTH_KEY = 'workmauriti_token';
const USER_KEY = 'workmauriti_user';

async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem(AUTH_KEY);
  const headers = new Headers(options.headers || {});

  if (!(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(payload?.message || 'Erro ao comunicar com a API');
  }

  return payload as T;
}

class QueryBuilder<T = any> implements PromiseLike<ApiResult<T>> {
  private table: string;
  private action: 'select' | 'insert' | 'update' | 'delete' = 'select';
  private columns = '*';
  private payload: unknown = null;
  private filters: QueryFilter[] = [];
  private orderBy: QueryOrder | null = null;
  private rowLimit: number | null = null;
  private singleResult = false;
  private allowNullSingle = false;

  constructor(table: string) {
    this.table = table;
  }

  select(columns = '*') {
    this.action = 'select';
    this.columns = columns;
    return this;
  }

  insert(values: unknown) {
    this.action = 'insert';
    this.payload = values;
    return this;
  }

  update(values: unknown) {
    this.action = 'update';
    this.payload = values;
    return this;
  }

  delete() {
    this.action = 'delete';
    return this;
  }

  eq(coluna: string, valor: unknown) {
    this.filters.push({ coluna, operador: 'eq', valor });
    return this;
  }

  neq(coluna: string, valor: unknown) {
    this.filters.push({ coluna, operador: 'neq', valor });
    return this;
  }

  order(coluna: string, options: { ascending?: boolean } = {}) {
    this.orderBy = { coluna, crescente: options.ascending !== false };
    return this;
  }

  limit(limit: number) {
    this.rowLimit = limit;
    return this;
  }

  single() {
    this.singleResult = true;
    this.allowNullSingle = false;
    return this.execute();
  }

  maybeSingle() {
    this.singleResult = true;
    this.allowNullSingle = true;
    return this.execute();
  }

  async execute(): Promise<ApiResult<T>> {
    try {
      if (this.action === 'select') {
        const params = new URLSearchParams();
        params.set('colunas', this.columns);
        if (this.filters.length) params.set('filtros', JSON.stringify(this.filters));
        if (this.orderBy) params.set('ordem', JSON.stringify(this.orderBy));
        if (this.rowLimit !== null) params.set('limite', String(this.rowLimit));

        const result = await apiRequest<{ data: any[] }>(`/dados/${this.table}?${params.toString()}`);
        const rows = result.data || [];

        if (this.singleResult) {
          if (!rows.length && !this.allowNullSingle) {
            return { data: null, error: new Error('Registro não encontrado') };
          }
          return { data: (rows[0] || null) as T, error: null };
        }

        return { data: rows as T, error: null };
      }

      const method = this.action === 'insert' ? 'POST' : this.action === 'update' ? 'PATCH' : 'DELETE';
      const result = await apiRequest<{ data: any }>(`/dados/${this.table}`, {
        method,
        body: JSON.stringify({
          valores: this.payload,
          filtros: this.filters,
        }),
      });

      return { data: result.data as T, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  then<TResult1 = ApiResult<T>, TResult2 = never>(
    onfulfilled?: ((value: ApiResult<T>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null,
  ): PromiseLike<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected);
  }
}

function getStoredUser() {
  const raw = localStorage.getItem(USER_KEY);
  return raw ? JSON.parse(raw) : null;
}

export const apiSite = {
  from(table: string) {
    return new QueryBuilder(table);
  },

  auth: {
    async signInWithPassword(credentials: { email: string; password: string }) {
      try {
        const result = await apiRequest<{ token: string; user: any }>('/auth/login', {
          method: 'POST',
          body: JSON.stringify(credentials),
        });
        localStorage.setItem(AUTH_KEY, result.token);
        localStorage.setItem(USER_KEY, JSON.stringify(result.user));
        return { data: { session: { access_token: result.token, user: result.user }, user: result.user }, error: null };
      } catch (error) {
        return { data: { session: null, user: null }, error };
      }
    },

    async signOut() {
      localStorage.removeItem(AUTH_KEY);
      localStorage.removeItem(USER_KEY);
      return { error: null };
    },

    async getSession() {
      const token = localStorage.getItem(AUTH_KEY);
      const user = getStoredUser();
      return { data: { session: token && user ? { access_token: token, user } : null }, error: null };
    },

    async getUser() {
      try {
        const token = localStorage.getItem(AUTH_KEY);
        if (!token) return { data: { user: null }, error: null };
        const result = await apiRequest<{ user: any }>('/auth/me');
        localStorage.setItem(USER_KEY, JSON.stringify(result.user));
        return { data: { user: result.user }, error: null };
      } catch (error) {
        localStorage.removeItem(AUTH_KEY);
        localStorage.removeItem(USER_KEY);
        return { data: { user: null }, error };
      }
    },

    onAuthStateChange(callback: (_event: string, session: any) => void) {
      this.getSession().then(({ data }) => callback('INITIAL_SESSION', data.session));
      return {
        data: {
          subscription: {
            unsubscribe: () => undefined,
          },
        },
      };
    },

    async resetPasswordForEmail(email: string, _options?: Record<string, unknown>) {
      try {
        await apiRequest('/auth/recuperar-senha', {
          method: 'POST',
          body: JSON.stringify({ email }),
        });
        return { data: {}, error: null };
      } catch (error) {
        return { data: null, error };
      }
    },

    async updateUser(values: { email?: string; password?: string }) {
      try {
        const result = await apiRequest<{ user: any }>('/auth/me', {
          method: 'PATCH',
          body: JSON.stringify(values),
        });
        localStorage.setItem(USER_KEY, JSON.stringify(result.user));
        return { data: { user: result.user }, error: null };
      } catch (error) {
        return { data: null, error };
      }
    },
  },

  storage: {
    from(bucket: string) {
      return {
        async upload(path: string, file: File, _options?: Record<string, unknown>) {
          try {
            const formData = new FormData();
            formData.append('arquivo', file);
            formData.append('caminho', path);
            const result = await apiRequest<{ data: { path: string } }>(`/arquivos/${bucket}`, {
              method: 'POST',
              body: formData,
            });
            return { data: result.data, error: null };
          } catch (error) {
            return { data: null, error };
          }
        },

        getPublicUrl(path: string) {
          const publicUrl = `${API_URL}/uploads/${bucket}/${path}`.replace(/([^:]\/)\/+/g, '$1');
          return { data: { publicUrl } };
        },

        async list(options?: { folder?: string }) {
          try {
            const query = options?.folder ? `?folder=${encodeURIComponent(options.folder)}` : '';
            const result = await apiRequest<{ data: Array<{ path: string; url: string; name: string; size: number; modified_at: string }> }>(`/arquivos/${bucket}${query}`);
            return { data: result.data, error: null };
          } catch (error) {
            return { data: null, error };
          }
        },

        async remove(paths: string[]) {
          try {
            await apiRequest(`/arquivos/${bucket}`, {
              method: 'DELETE',
              body: JSON.stringify({ caminhos: paths }),
            });
            return { data: {}, error: null };
          } catch (error) {
            return { data: null, error };
          }
        },
      };
    },
  },

  channel(_name?: string) {
    return {
      on(_event: string, _filter: unknown, _callback: unknown) {
        return this;
      },
      subscribe() {
        return this;
      },
    };
  },

  removeChannel(_channel?: unknown) {
    return undefined;
  },
};
